
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface RescheduleData {
  bookingId: string;
  newDate: string;
  newStartTime: string;
  newEndTime: string;
  timeSlotId?: string;
}

export const useBookingReschedule = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ bookingId, newDate, newStartTime, newEndTime, timeSlotId }: RescheduleData) => {
      console.log('Rescheduling booking:', { bookingId, newDate, newStartTime, newEndTime, timeSlotId });

      // Get the current booking details first to know the original time slot
      const { data: currentBooking, error: fetchError } = await supabase
        .from('bookings')
        .select('booking_date, start_time, end_time, time_slot_id')
        .eq('id', bookingId)
        .single();

      if (fetchError) {
        console.error('Error fetching current booking:', fetchError);
        throw new Error('Failed to fetch current booking details');
      }

      console.log('Current booking details:', currentBooking);

      // If the booking has a time_slot_id, we need to decrease its count
      if (currentBooking.time_slot_id) {
        console.log('Releasing old time slot:', currentBooking.time_slot_id);
        
        // Get current bookings count for the old slot
        const { data: oldSlot, error: oldSlotFetchError } = await supabase
          .from('time_slots')
          .select('current_bookings')
          .eq('id', currentBooking.time_slot_id)
          .single();

        if (oldSlotFetchError) {
          console.error('Error fetching old slot:', oldSlotFetchError);
        } else {
          // Decrease the booking count for the old slot
          const { error: oldSlotUpdateError } = await supabase
            .from('time_slots')
            .update({
              current_bookings: Math.max(0, oldSlot.current_bookings - 1)
            })
            .eq('id', currentBooking.time_slot_id);

          if (oldSlotUpdateError) {
            console.error('Error updating old time slot:', oldSlotUpdateError);
          } else {
            console.log('Successfully released old time slot');
          }
        }
      }

      // Update the booking with new date, time, and time slot
      const { data: updatedBooking, error: updateError } = await supabase
        .from('bookings')
        .update({
          booking_date: newDate,
          start_time: newStartTime,
          end_time: newEndTime,
          time_slot_id: timeSlotId || null, // Set new time slot ID or null
          updated_at: new Date().toISOString()
        })
        .eq('id', bookingId)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating booking:', updateError);
        throw new Error('Failed to reschedule booking');
      }

      console.log('Booking updated successfully:', updatedBooking);

      // If there's a new time slot ID, increment its current bookings
      if (timeSlotId) {
        console.log('Updating new time slot:', timeSlotId);
        
        // Get current bookings count for the new slot
        const { data: newSlot, error: newSlotFetchError } = await supabase
          .from('time_slots')
          .select('current_bookings')
          .eq('id', timeSlotId)
          .single();

        if (newSlotFetchError) {
          console.error('Error fetching new slot:', newSlotFetchError);
        } else {
          // Increment the booking count for the new slot
          const { error: newSlotUpdateError } = await supabase
            .from('time_slots')
            .update({
              current_bookings: newSlot.current_bookings + 1
            })
            .eq('id', timeSlotId);

          if (newSlotUpdateError) {
            console.error('Error updating new time slot:', newSlotUpdateError);
            // Note: We don't throw here as the booking update was successful
          } else {
            console.log('Successfully updated new time slot');
          }
        }
      }

      return updatedBooking;
    },
    onSuccess: () => {
      toast({
        title: "Booking Rescheduled",
        description: "Your booking has been successfully rescheduled. You'll receive a confirmation email shortly.",
      });
      
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ["booking"] });
      queryClient.invalidateQueries({ queryKey: ["bookings"] });
      queryClient.invalidateQueries({ queryKey: ["time-slots"] });
      queryClient.invalidateQueries({ queryKey: ["available-time-slots"] });
    },
    onError: (error) => {
      console.error('Error rescheduling booking:', error);
      toast({
        title: "Reschedule Failed",
        description: "Failed to reschedule your booking. Please try again or contact support.",
        variant: "destructive",
      });
    },
  });
};
